/**
 * POS Properties — shared constants
 */

import { getCountryOptions } from '../../../../lib/countries';
import { POS_GOLD } from '../../../../config/posRoomStatus';

// POS Brand Colors
export { POS_GOLD };
export const POS_GOLD_DARK = '#6E5A1B';      // Body text / strong labels on light backgrounds
export const POS_GOLD_SOFT_BG = '#FBF7EB';   // Banners, gentle fills
export const POS_GOLD_LIGHT_BG = '#FAF0CC';  // Highlighted current-step rows
export const POS_GOLD_BORDER = '#EAD993';    // Borders on tinted blocks
export const POS_GOLD_TRACK = '#F5E8B8';     // Progress bar track

// Permission constants
export const PERMISSIONS = {
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete'
} as const;

interface StatusConfigEntry {
  label: string;
  color: string;
  bg: string;
}

// Contract status configuration. Four values in use across DK + ZA:
// prospective / active / onboarding / expired. 'cancelled' was carried in
// legacy code but has zero rows in production and no writer in the codebase —
// dropped during the 2026-05-11 cleanup so the dropdown and pill registry align.
//
// 'prospective' (2026-07-28) means the property exists and a proposal is with
// the client, but they have committed to nothing. It carries the same slate
// tone the canonical palette gives Draft (config/statusConfig.ts), because it
// is the same idea: real record, nothing agreed. A prospective property holds
// no charter number — that is issued when it commits and leaves this status.
export const STATUS_CONFIG: Record<string, StatusConfigEntry> = {
  prospective: { label: 'Prospective', color: 'var(--color-text-light)', bg: 'var(--color-bg)' },
  active: { label: 'Active', color: 'var(--color-success)', bg: 'var(--color-success-bg)' },
  onboarding: { label: 'Onboarding', color: 'var(--color-warning)', bg: 'var(--color-warning-bg)' },
  expired: { label: 'Expired', color: 'var(--color-error)', bg: 'var(--color-error-bg)' },
};

// Property temperature — one source of truth, derived purely from the
// assurance expiry date. Used by the property list, the property modal and
// the renewals board so they can never disagree. This is the PROPERTY
// assurance temperature, distinct from the customer engagement "temperature".
export type Temperature = 'green' | 'yellow' | 'orange' | 'red';

export function computeTemperature(assuredUntil: string | null | undefined): Temperature | null {
  if (!assuredUntil) return null;
  const days = Math.floor((new Date(assuredUntil).getTime() - Date.now()) / 86400000);
  if (days < 0) return 'red';
  if (days <= 30) return 'orange';
  if (days <= 90) return 'yellow';
  return 'green';
}

export const TEMPERATURE_CONFIG: Record<Temperature, { label: string; color: string; bg: string }> = {
  green: { label: 'Green', color: 'var(--color-success)', bg: 'var(--color-success-bg)' },
  yellow: { label: 'Yellow', color: '#a16207', bg: '#fef9c3' },
  orange: { label: 'Orange', color: 'var(--color-warning-text)', bg: '#ffedd5' },
  red: { label: 'Red', color: 'var(--color-error)', bg: 'var(--color-error-bg)' },
};

// Renewals board COLUMNS. The "to contact" stage is split into the three
// temperature bands, so un-worked properties flow Expiring soon -> Due soon
// -> Expired on their own as the clock runs. Once you act on one it moves
// into the manual sales stages (Contacted, Quoted), then Dormant for parked.
// A renewed property cools to green and drops off the board. Stored in
// hosp_properties.renewal_stage: null = to-contact (placed by temperature),
// then 'contacted', 'quoted', 'dormant'. The colour is the column header
// accent (the documented data-driven inline exception).
export interface RenewalColumn { key: string; label: string; color: string; }
export const RENEWAL_COLUMNS: RenewalColumn[] = [
  { key: 'yellow',    label: 'Expiring soon', color: '#a16207' },
  { key: 'orange',    label: 'Due soon',      color: 'var(--color-warning-text)' },
  { key: 'red',       label: 'Expired',       color: 'var(--color-error)' },
  { key: 'contacted', label: 'Contacted',     color: 'var(--color-info)' },
  { key: 'quoted',    label: 'Quoted',        color: 'var(--color-primary-light)' },
  { key: 'dormant',   label: 'Dormant',       color: 'var(--color-text-light)' },
];
export const DORMANT_STAGE = 'dormant';

interface TierConfigEntry {
  label: string;
  color: string;
  bg: string;
  frequency: string;
}

// Tier configuration with POS branding
export const TIER_CONFIG: Record<string, TierConfigEntry> = {
  signature: { label: 'Signature', color: 'var(--color-white)', bg: POS_GOLD, frequency: '3×/year' },
  distinguished: { label: 'Distinguished', color: 'var(--color-white)', bg: '#A8A9AD', frequency: '2×/year' },
  assured: { label: 'Assured', color: 'var(--color-white)', bg: '#CD7F32', frequency: '1×/year' }
};

interface SelectOption {
  value: string;
  label: string;
}

// Amenity options for directory listing (matches pos-directory.ts AMENITY_LABELS)
export const AMENITY_OPTIONS: SelectOption[] = [
  { value: 'pool', label: 'Swimming Pool' },
  { value: 'spa', label: 'Spa' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'bar', label: 'Bar' },
  { value: 'wifi', label: 'Free WiFi' },
  { value: 'parking', label: 'Free Parking' },
  { value: 'gym', label: 'Gym' },
  { value: 'room-service', label: 'Room Service' },
  { value: 'pet-friendly', label: 'Pet Friendly' },
  { value: 'airport-shuttle', label: 'Airport Shuttle' },
  { value: 'conference', label: 'Conference Facilities' },
  { value: 'beachfront', label: 'Beachfront' },
  { value: 'garden', label: 'Garden' },
  { value: 'mountain-view', label: 'Mountain View' },
  { value: 'heritage', label: 'Heritage Property' },
  { value: 'boutique', label: 'Boutique' },
  { value: 'family-friendly', label: 'Family Friendly' },
  { value: 'eco-certified', label: 'Eco Certified' },
  { value: 'bbq', label: 'BBQ / Braai' },
  { value: 'self-check-in', label: 'Self Check-in' },
  { value: 'workspace', label: 'Dedicated Workspace' },
];

// Listing link platform options
export const PLATFORM_OPTIONS: SelectOption[] = [
  { value: 'website', label: 'Website' },
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'booking', label: 'Booking.com' },
  { value: 'lekkeslaap', label: 'LekkeSlaap' },
  { value: 'tripadvisor', label: 'TripAdvisor' },
  { value: 'google', label: 'Google' },
  { value: 'other', label: 'Other' },
];

// Property types
export const PROPERTY_TYPES: SelectOption[] = [
  // Hospitality
  { value: 'hotel', label: 'Hotel' },
  { value: 'boutique_hotel', label: 'Boutique Hotel' },
  { value: 'guesthouse', label: 'Guest House' },
  { value: 'bnb', label: 'B&B' },
  { value: 'lodge', label: 'Lodge' },
  { value: 'safari_lodge', label: 'Safari Lodge' },
  { value: 'resort', label: 'Resort' },
  { value: 'spa_hotel', label: 'Spa Hotel' },
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'serviced_apartments', label: 'Serviced Apartments' },
  { value: 'holiday_rental', label: 'Holiday Rental' },
  // Community Living
  { value: 'school_boarding', label: 'School Boarding House' },
  { value: 'university_residence', label: 'University Residence' },
  { value: 'student_accommodation', label: 'Student Accommodation' },
  { value: 'sports_academy', label: 'Sports Academy' },
  { value: 'military_barracks', label: 'Military Barracks' },
  { value: 'worker_accommodation', label: 'Worker Accommodation' },
  { value: 'care_facility', label: 'Care Facility' },
  { value: 'hostel', label: 'Hostel' },
  // General
  { value: 'other', label: 'Other' },
];

interface MatrixStatusEntry {
  label: string;
  color: string;
}

// Matrix status configuration (for rooms service matrix)
export const MATRIX_STATUS_CONFIG: Record<string, MatrixStatusEntry> = {
  pending: { label: 'Not Serviced', color: 'var(--color-warning)' },
  booked: { label: 'Booked', color: '#8b5cf6' },
  certified: { label: 'Certified', color: 'var(--color-success)' },
  'due-soon': { label: 'Due Soon', color: 'var(--color-warning)' },
  overdue: { label: 'Overdue', color: 'var(--color-error)' },
  future: { label: 'Future', color: 'var(--color-border)' }
};

// Tier frequencies (days between services)
export const TIER_FREQUENCIES: Record<string, number> = {
  assured: 365,
  distinguished: 180,
  signature: 120
};

// Get ISO week number
export function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

// Country options for directory publishing — centralised in lib/countries.ts
export const COUNTRY_OPTIONS: SelectOption[] = getCountryOptions();

// Region mapping (province -> 2-letter code) for directory URLs
export const REGION_NAMES: Record<string, Record<string, string>> = {
  za: {
    wc: 'Western Cape', gt: 'Gauteng', ec: 'Eastern Cape',
    kzn: 'KwaZulu-Natal', lp: 'Limpopo', nw: 'North West',
    mp: 'Mpumalanga', fs: 'Free State', nc: 'Northern Cape',
  },
  dk: {
    sj: 'Sjælland', jy: 'Jylland', fy: 'Fyn',
  },
};

interface DirectoryProperty {
  is_published?: boolean;
  slug?: string;
  country?: string;
  province?: string;
}

/**
 * Build the public directory URL for a property, or null if not publishable.
 * URL pattern: https://peaceofsleep.vip/{country}/{region}/{slug}/
 * (Pure geographic — matches the cleanmybed.com convention.)
 */
export function getDirectoryUrl(property: DirectoryProperty): string | null {
  if (!property.is_published || !property.slug || !property.country) return null;
  const countryRegions = REGION_NAMES[property.country?.toLowerCase()];
  if (!countryRegions) return null;
  const province = (property.province || '').toLowerCase().trim();
  let regionCode: string | null = null;
  for (const [code, name] of Object.entries(countryRegions)) {
    if (name.toLowerCase() === province) { regionCode = code; break; }
  }
  if (!regionCode) return null;
  return `https://peaceofsleep.vip/${property.country.toLowerCase()}/${regionCode}/${property.slug}/`;
}

interface TierBadgeProps {
  tier: string;
}

// Tier Badge Component
export function TierBadge({ tier }: TierBadgeProps) {
  const config = TIER_CONFIG[tier] || { label: tier || 'None', color: 'var(--color-text-light)', bg: 'var(--color-border)' };
  return (
    <span className="tier-badge" style={{ color: config.color, background: config.bg }}>
      {config.label}
    </span>
  );
}
